import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import { canWrite, canManageSupplierPayments, WRITE_PERMISSION_DENIED } from '@/services/permissions';
import { recordAudit } from '@/services/audit';

export interface Supplier {
  id: string;
  companyName: string;
  contactName: string;
  phone: string;
  email: string;
  creditStatus: string;
  createdAt: string;
}

export interface SuppliersResult {
  data: Supplier[];
}

// Consulta los proveedores reales desde `suppliers`. Sin datos reales devuelve
// un array vacío (honesto) en vez de inventar mocks, para no confundir al admin.
export async function fetchSuppliers(): Promise<SuppliersResult> {
  if (!isSupabaseConfigured || !supabase) {
    return { data: [] };
  }

  try {
    const { data, error } = await supabase.from('suppliers').select('*');

    if (error) {
      throw new Error(error.message);
    }

    const rows = (data ?? []) as Array<Record<string, any>>;
    const mapped: Supplier[] = rows.map((r) => ({
      id: r.id,
      companyName: r.company_name,
      contactName: r.contact_name ?? '',
      phone: r.phone ?? '',
      email: r.email ?? '',
      creditStatus: r.credit_status ?? '',
      createdAt: r.created_at ?? '',
    }));

    return { data: mapped };
  } catch (err) {
    console.error('[suppliers] connection failed:', err);
    throw err;
  }
}

// Entrada del formulario de proveedor (crear).
export interface SupplierInput {
  companyName: string;
  contactName: string;
  phone: string;
  email: string;
}

// Crea un proveedor nuevo en Supabase.
export async function saveSupplier(
  input: SupplierInput,
): Promise<{ data: Supplier | null; error: string | null }> {
  if (!(await canWrite('suppliers'))) {
    return { data: null, error: WRITE_PERMISSION_DENIED };
  }
  if (!isSupabaseConfigured || !supabase) {
    return { data: null, error: 'Supabase no está configurado.' };
  }

  const payload = {
    company_name: input.companyName.trim(),
    contact_name: input.contactName.trim() || null,
    phone: input.phone.trim() || null,
    email: input.email.trim() || null,
  };

  try {
    const { data, error } = await supabase.from('suppliers').insert(payload).select('*');
    if (error) return { data: null, error: error.message };
    const row = data?.[0] as Record<string, any> | undefined;
    if (!row) return { data: null, error: 'No se pudo leer el proveedor guardado.' };
    recordAudit({
      entityType: 'suppliers',
      entityId: row.id,
      action: 'CREATE',
      newValue: input.companyName,
    });
    return {
      data: {
        id: row.id,
        companyName: row.company_name,
        contactName: row.contact_name ?? '',
        phone: row.phone ?? '',
        email: row.email ?? '',
        creditStatus: row.credit_status ?? '',
        createdAt: row.created_at ?? '',
      },
      error: null,
    };
  } catch (err) {
    console.warn('[suppliers] save failed:', err);
    return { data: null, error: 'Error de conexión al guardar el proveedor.' };
  }
}

// Actualiza un proveedor existente.
export async function updateSupplier(
  id: string,
  input: SupplierInput,
): Promise<{ data: Supplier | null; error: string | null }> {
  if (!(await canWrite('suppliers'))) {
    return { data: null, error: WRITE_PERMISSION_DENIED };
  }
  if (!isSupabaseConfigured || !supabase) {
    return { data: null, error: 'Supabase no está configurado.' };
  }

  const payload = {
    company_name: input.companyName.trim(),
    contact_name: input.contactName.trim() || null,
    phone: input.phone.trim() || null,
    email: input.email.trim() || null,
  };

  try {
    const { data, error } = await supabase.from('suppliers').update(payload).eq('id', id).select('*');
    if (error) return { data: null, error: error.message };
    const row = data?.[0] as Record<string, any> | undefined;
    recordAudit({
      entityType: 'suppliers',
      entityId: id,
      action: 'UPDATE',
      newValue: input.companyName,
    });
    return {
      data: row
        ? {
            id: row.id,
            companyName: row.company_name,
            contactName: row.contact_name ?? '',
            phone: row.phone ?? '',
            email: row.email ?? '',
            creditStatus: row.credit_status ?? '',
            createdAt: row.created_at ?? '',
          }
        : null,
      error: null,
    };
  } catch (err) {
    console.warn('[suppliers] update failed:', err);
    return { data: null, error: 'Error de conexión al actualizar el proveedor.' };
  }
}

// --- Abonos / pagos a proveedores ---
// Espejo del sistema de consignaciones: permite registrar pagos parciales a un
// proveedor y derivar su estado de crédito (Al día / Pendiente de pago / Pago
// parcial) a partir de las compras adeudadas.

export interface SupplierPayment {
  id: string;
  supplierId: string;
  amount: number;
  paidAt: string;
  notes: string;
  createdAt: string;
}

export interface SupplierBalance {
  totalPurchases: number;
  totalPaid: number;
  remaining: number;
  status: string;
}

export async function fetchSupplierPayments(supplierId: string): Promise<SupplierPayment[]> {
  if (!isSupabaseConfigured || !supabase) return [];
  const { data, error } = await supabase
    .from('supplier_payments')
    .select('*')
    .eq('supplier_id', supplierId)
    .order('paid_at', { ascending: false, nullsFirst: false });
  if (error) {
    console.warn('[suppliers] fetchSupplierPayments error', error.message);
    return [];
  }
  return ((data ?? []) as Array<Record<string, any>>).map((p) => ({
    id: p.id,
    supplierId: p.supplier_id,
    amount: Number(p.amount),
    paidAt: p.paid_at ?? '',
    notes: p.notes ?? '',
    createdAt: p.created_at ?? '',
  }));
}

export async function fetchSupplierBalance(supplierId: string): Promise<SupplierBalance> {
  if (!isSupabaseConfigured || !supabase) {
    return { totalPurchases: 0, totalPaid: 0, remaining: 0, status: 'Al día' };
  }
  const [purchasesRes, paymentsRes] = await Promise.all([
    supabase.from('purchases').select('total_cost').eq('supplier_id', supplierId),
    supabase.from('supplier_payments').select('amount').eq('supplier_id', supplierId),
  ]);
  const totalPurchases = ((purchasesRes.data ?? []) as Array<Record<string, any>>).reduce(
    (s, p) => s + Number(p.total_cost ?? 0),
    0,
  );
  const totalPaid = ((paymentsRes.data ?? []) as Array<Record<string, any>>).reduce(
    (s, p) => s + Number(p.amount ?? 0),
    0,
  );
  const remaining = Math.max(0, totalPurchases - totalPaid);
  const status = remaining <= 0 ? 'Al día' : totalPaid <= 0 ? 'Pendiente de pago' : 'Pago parcial';
  return { totalPurchases, totalPaid, remaining, status };
}

// Recalcula el estado de crédito del proveedor según sus compras vs abonos.
export async function recalculateSupplierCreditStatus(supplierId: string): Promise<string> {
  if (!isSupabaseConfigured || !supabase) return '';
  const balance = await fetchSupplierBalance(supplierId);
  await supabase.from('suppliers').update({ credit_status: balance.status }).eq('id', supplierId);
  return balance.status;
}

export async function addSupplierPayment(input: {
  supplierId: string;
  amount: number;
  paidAt: string;
  notes: string;
}): Promise<{ error: string | null }> {
  if (!(await canManageSupplierPayments())) return { error: WRITE_PERMISSION_DENIED };
  if (!isSupabaseConfigured || !supabase) return { error: 'Supabase no está configurado.' };

  const { error } = await supabase.from('supplier_payments').insert({
    supplier_id: input.supplierId,
    amount: Number(input.amount),
    paid_at: input.paidAt || null,
    notes: input.notes.trim() || null,
  });
  if (error) return { error: error.message };

  recordAudit({
    entityType: 'suppliers',
    entityId: input.supplierId,
    action: 'UPDATE',
    fieldName: 'payment',
    newValue: `$${input.amount}`,
  });
  await recalculateSupplierCreditStatus(input.supplierId);
  return { error: null };
}

export async function deleteSupplierPayment(
  id: string,
  supplierId: string,
): Promise<{ error: string | null }> {
  if (!(await canManageSupplierPayments())) return { error: WRITE_PERMISSION_DENIED };
  if (!isSupabaseConfigured || !supabase) return { error: 'Supabase no está configurado.' };

  const { error } = await supabase.from('supplier_payments').delete().eq('id', id);
  if (error) return { error: error.message };

  recordAudit({
    entityType: 'suppliers',
    entityId: supplierId,
    action: 'UPDATE',
    fieldName: 'payment',
    oldValue: 'deleted',
    newValue: '',
  });
  await recalculateSupplierCreditStatus(supplierId);
  return { error: null };
}